class SCountryModal extends HTMLElement {
  constructor() {
    super();
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.opener = null;
  }

  connectedCallback() {
    this.input = this.querySelector('[data-country-input]');
    this.current = this.querySelector('[data-country-current]');
    this.toggle = this.querySelector('[data-country-toggle]');
    this.dropdown = this.querySelector('[data-country-dropdown]');
    this.options = this.querySelector('[data-country-options]');
    this.search = this.querySelector('[data-country-search]');
    this.noResults = this.querySelector('[data-country-no-results]');
    this.loadError = this.querySelector('[data-pz-country-error]');

    /* Spec 2026-10-13 section 5.6 item 3 (R34; WOOPF ADR 0005, amended 2026-09-28): the PURRZ header's globe and
       the phone menu's Region tile open this pop-up through the same PubSub bridge as the cart drawer and the
       search dialog. The element focused when the message arrives (the globe, or the burger) gets focus back on
       close. One subscription, even when a Theme Editor reload connects this element again. */
    if (window.PubSub && typeof window.PubSub.subscribe === 'function') {
      this.unsubscribeOpen?.();
      this.unsubscribeOpen = window.PubSub.subscribe('country-modal:open', () => {
        this.opener = document.activeElement;
        this.openerIsIcon = false;
        this.open();
      });
    }

    document.addEventListener('click', (e) => {
      const opener = e.target.closest('.js-country-modal-open');
      if (opener) {
        this.opener = opener;
        /* The legacy icon is this dialog's disclosure, so it reports aria-expanded; a topic opener does not. */
        this.openerIsIcon = true;
        this.open();
      }
      if (e.target.closest('[data-country-close]')) this.close();

      const option = e.target.closest('[data-country-option]');
      if (option && this.contains(option)) {
        this.selectCountry(option);
      }

      if (this.classList.contains('is-open') && this.dropdown && !this.dropdown.hidden) {
        const clickedInsideField = e.target.closest('.country-modal__field');
        if (!clickedInsideField && this.contains(e.target)) {
          this.closeDropdown();
        }
      }
    });

    this.toggle?.addEventListener('click', () => {
      this.toggleDropdown();
    });

    this.search?.addEventListener('input', () => {
      this.filterCountries();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) {
        if (this.dropdown && !this.dropdown.hidden) {
          this.closeDropdown();
          return;
        }

        this.close();
      }
    });
  }

  /* #220 (Jeff, 2026-10-26): the flags are Shopify's images, drawn in the markup by
     snippets/purrz-country-flag.liquid. The emoji this file used to write into every
     [data-country-code] showed as two letters on Windows, and its fallback was a globe emoji. */

  setExpanded(isOpen) {
    this.toggle?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  toggleDropdown() {
    if (!this.dropdown) return;

    this.dropdown.hidden = !this.dropdown.hidden;
    this.setExpanded(!this.dropdown.hidden);

    if (!this.dropdown.hidden) {
      /* PERF-02: a list that failed to load tries again when the shopper opens it. */
      this.loadOptions();
      setTimeout(() => {
        this.search?.focus();
      }, 50);
    }
  }

  closeDropdown() {
    if (this.dropdown) {
      this.dropdown.hidden = true;
    }

    this.setExpanded(false);

    if (this.search) {
      this.search.value = '';
    }

    this.filterCountries();
  }

  filterCountries() {
    const query = (this.search?.value || '').trim().toLowerCase();
    const options = Array.from(this.querySelectorAll('[data-country-option]'));
    let visibleCount = 0;

    options.forEach((option) => {
      const value = (option.dataset.countrySearchValue || option.textContent || '').toLowerCase();
      const isVisible = !query || value.includes(query);

      option.hidden = !isVisible;

      if (isVisible) {
        visibleCount += 1;
      }
    });

    if (this.noResults) {
      /* PERF-02: no rows means the list has not loaded, not that the search matched nothing. */
      this.noResults.hidden = visibleCount !== 0 || options.length === 0;
    }
  }

  /* PERF-02 (PURRZ performance plan 2026-10-28; ADR 0014, the second bounded edit to this file): the 237 rows,
     228 KB and about 1,400 nodes, no longer print on every page. sections/purrz-country-options.liquid holds them,
     fetched through the Section Rendering API from [data-pz-country-src], which keeps the market's root_url. One
     request while it is pending and none once it lands; a failure shows [data-pz-country-error] and the next open
     tries again. The click handler is delegated, and filterCountries and selectCountry query the rows when they
     run, so the injected rows need no binding. A list with no source (the loop restored: ADR 0014's rollback)
     already holds its rows, and nothing is fetched. */
  loadOptions() {
    const src = this.options?.dataset.pzCountrySrc;
    if (!src) return Promise.resolve(true);
    if (this.optionsLoad) return this.optionsLoad;

    this.options.setAttribute('aria-busy', 'true');
    if (this.loadError) this.loadError.hidden = true;

    this.optionsLoad = fetch(src, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`Country list: HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const rows = Array.from(doc.querySelectorAll('[data-country-option]'));
        if (!rows.length) throw new Error('Country list: no rows in the response');

        /* The row of the country the form submits is the selected one, whatever the response marked. */
        const code = this.input?.value || '';
        rows.forEach((row) => {
          const isCurrent = row.dataset.countryCode === code;
          if (isCurrent) row.classList.add('is-active');
          else row.classList.remove('is-active');
          row.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
        });

        this.options.replaceChildren(...rows);
        this.options.removeAttribute('aria-busy');
        this.filterCountries();
        return true;
      })
      .catch(() => {
        this.optionsLoad = null;
        this.options.removeAttribute('aria-busy');
        if (this.loadError) this.loadError.hidden = false;
        return false;
      });

    return this.optionsLoad;
  }

  selectCountry(option) {
    const code = option.dataset.countryCode || '';
    const label = option.dataset.countryLabel || option.textContent.trim();

    if (this.input) {
      this.input.value = code;
    }

    if (this.current) {
      this.current.textContent = label;
    }

    if (this.toggle) {
      this.toggle.dataset.countryCode = code;

      /* #220: the button shows the picked row's flag, a copy of that row's image. */
      const triggerFlag = this.toggle.querySelector('.js-country-flag');
      const optionFlag = option.querySelector('.js-country-flag img');
      if (triggerFlag && optionFlag) {
        triggerFlag.replaceChildren(optionFlag.cloneNode(true));
      }
    }

    this.querySelectorAll('[data-country-option]').forEach((el) => {
      el.classList.remove('is-active');
      el.setAttribute('aria-selected', 'false');
    });

    option.classList.add('is-active');
    option.setAttribute('aria-selected', 'true');
    this.closeDropdown();
  }

  open() {
    /* A detached root (a Theme Editor reload) must never claim to be open. */
    if (!this.isConnected) return;

    /* PERF-02: the rows load on the first open, without holding the dialog back. */
    this.loadOptions();
    this.hidden = false;
    document.body.classList.add('modal-open');
    if (this.openerIsIcon !== false) this.opener?.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      this.classList.add('is-open');
      this.toggle?.focus();
      /* The shared dialog helper, where a theme has one (this theme ships none: the call is inert here). */
      window.CDialog?.open({
        root: this,
        initialFocus: this.toggle,
        returnFocus: this.opener,
        fallbackFocus: this.opener
      });
    });
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('modal-open');
    if (this.openerIsIcon !== false) this.opener?.setAttribute('aria-expanded', 'false');
    window.CDialog?.close(this, { restoreFocus: false });

    setTimeout(() => {
      this.hidden = true;
      this.closeDropdown();
      /* Send focus back to the icon that opened it, so keyboard users do not
         land at the top of the document. */
      this.opener?.focus();
      this.opener = null;
    }, 220);
  }

  /* Release the country-modal:open subscription when a Theme Editor reload replaces this element. */
  disconnectedCallback() {
    window.CDialog?.close(this, { restoreFocus: false });
    this.unsubscribeOpen?.();
    this.unsubscribeOpen = null;
  }
}

if (!customElements.get('s-country-modal')) {
  customElements.define('s-country-modal', SCountryModal);
}
